import { watch } from 'vue'
import type { App } from 'vue'
import { Constants } from '@/cfg/constants'


type ForumAuthPluginOptions = {
    store: any
    router: any
}

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $forumAuth: {
            login: (options?: any) => Promise<void>
            logout: (options?: any) => Promise<void>
            init: () => Promise<void>
            getAccessToken: (force?: boolean) => Promise<string | null>
            refreshSession: () => Promise<any | null>
            setInactivityLogoutEnabled: (enabled: boolean) => void
            isInactivityLogoutEnabled: () => boolean
            isAuthenticated: () => boolean
            isLoading: () => boolean
            getUser: () => any
            getClaims: () => any
            reset: () => void
        }
    }
}

export default {
    install(app: App, options: ForumAuthPluginOptions) {
        const { store, router } = options

        let initFinished = false
        let initPromise: Promise<void> | null = null
        let keepAliveInterval: ReturnType<typeof setInterval> | null = null
        let tokenRefreshTimeout: ReturnType<typeof setTimeout> | null = null
        let inactivityLogoutTimeout: ReturnType<typeof setTimeout> | null = null
        let activityListenersBound = false
        let lastActivityAt = Date.now()
        let auth0TokenExpiresIn: number | null = null
        let tokenExpiresAtMs: number | null = null
        let tokenRefreshLeadMs = 0
        let inactivityLogoutEnabled = false

        const USER_ACTIVITY_WINDOW_MS = 30 * 60 * 1000
        const KEEP_ALIVE_INTERVAL_MS = 60 * 1000

        const getAuth0 = () => app.config.globalProperties.$auth0

        const getTokenExpiryMs = (token?: string | null) => {
            if (!token || typeof window === 'undefined') {
                return null
            }

            try {
                const [, payload] = token.split('.')
                if (!payload) {
                    return null
                }

                const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
                const decodedPayload = window.atob(normalizedPayload)
                const claims = JSON.parse(decodedPayload)
                const expiresAtSeconds = Number(claims?.exp)

                if (!Number.isFinite(expiresAtSeconds) || expiresAtSeconds <= 0) {
                    return null
                }

                return expiresAtSeconds * 1000
            } catch (error) {
                console.warn('Failed to parse token expiry from stored token', error)
                return null
            }
        }

        const markActivity = () => {
            lastActivityAt = Date.now()
            scheduleInactivityLogout()
        }

        const isUserActive = () => (Date.now() - lastActivityAt) <= USER_ACTIVITY_WINDOW_MS

        const isSessionRoute = () => {
            const routeName = String(router?.currentRoute?.value?.name || '')
            return routeName === 'room' || routeName === 'logs'
        }

        const isSessionActive = () => {
            const inConference = !!store?.state?.inConference
            const hasEvent = !!store?.state?.event?.event?.id
            return inConference || hasEvent || isSessionRoute()
        }

        const shouldRefreshSession = () => {
            const tabVisible = typeof document === 'undefined' ? true : document.visibilityState === 'visible'
            return tabVisible && (isUserActive() || isSessionActive())
        }

        const shouldBypassInactivityLogout = () => isSessionActive()

        const bindActivityListeners = () => {
            if (activityListenersBound || typeof window === 'undefined') {
                return
            }

            ['click', 'keydown', 'mousemove', 'touchstart', 'focus'].forEach((eventName) => {
                window.addEventListener(eventName, markActivity, { passive: true })
            })

            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', markActivity)
            }

            activityListenersBound = true
        }

        const unbindActivityListeners = () => {
            if (!activityListenersBound || typeof window === 'undefined') {
                return
            }

            ['click', 'keydown', 'mousemove', 'touchstart', 'focus'].forEach((eventName) => {
                window.removeEventListener(eventName, markActivity)
            })

            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', markActivity)
            }

            activityListenersBound = false
        }

        const stopKeepAlive = () => {
            if (keepAliveInterval) {
                clearInterval(keepAliveInterval)
                keepAliveInterval = null
            }
        }

        const stopTokenRefreshTimer = () => {
            if (tokenRefreshTimeout) {
                clearTimeout(tokenRefreshTimeout)
                tokenRefreshTimeout = null
            }
        }

        const stopInactivityLogoutTimer = () => {
            if (inactivityLogoutTimeout) {
                clearTimeout(inactivityLogoutTimeout)
                inactivityLogoutTimeout = null
            }
        }

        const setInactivityLogoutEnabled = (enabled: boolean) => {
            inactivityLogoutEnabled = enabled

            if (!inactivityLogoutEnabled) {
                stopInactivityLogoutTimer()
                return
            }

            scheduleInactivityLogout()
        }

        const scheduleInactivityLogout = () => {
            if (!inactivityLogoutEnabled) {
                return
            }

            stopInactivityLogoutTimer()
            console.log('Scheduling inactivity logout. Last activity at:', new Date(lastActivityAt).toISOString(), 'Current time:', new Date().toISOString())
            const auth0 = getAuth0()
            if (!auth0?.isAuthenticated?.value) {
                return
            }

            if (shouldBypassInactivityLogout()) {
                return
            }

            inactivityLogoutTimeout = setTimeout(() => {
                void hardLogout()
            }, USER_ACTIVITY_WINDOW_MS)
        }

        const scheduleTokenRefresh = (expiresInSeconds?: number | null) => {
            stopTokenRefreshTimer()

            const auth0 = getAuth0()
            if (!auth0?.isAuthenticated?.value) {
                return
            }

            if (!shouldRefreshSession()) {
                return
            }

            const safeExpiresInSeconds = Number.isFinite(Number(expiresInSeconds))
                ? Math.max(Number(expiresInSeconds), 0)
                : 0
            const tokenLifetimeMs = safeExpiresInSeconds * 1000

            auth0TokenExpiresIn = safeExpiresInSeconds
            tokenExpiresAtMs = Date.now() + tokenLifetimeMs
            tokenRefreshLeadMs = Math.floor(tokenLifetimeMs * 0.33)

            const refreshInMs = Math.max(tokenLifetimeMs - tokenRefreshLeadMs, 0)
            tokenRefreshTimeout = setTimeout(() => {
                void runTokenRefreshCheck()
            }, refreshInMs)
        }

        const syncStoreToken = async (token?: string | null, expiresIn?: number | null) => {
            if (!token) {
                return
            }

            if (store?.state?.auth?.token !== token) {
                store.commit('auth/setToken', token)
            }

            scheduleTokenRefresh(expiresIn)
            scheduleInactivityLogout()
        }

        const hardLogout = async () => {
            stopKeepAlive()
            unbindActivityListeners()
            stopTokenRefreshTimer()
            stopInactivityLogoutTimer()
            auth0TokenExpiresIn = null
            tokenExpiresAtMs = null
            tokenRefreshLeadMs = 0

            await store.dispatch('auth/logout').catch((err: any) => {
                console.warn('Local logout cleanup failed', err)
            })

            const auth0 = getAuth0()
            if (auth0?.isAuthenticated?.value) {
                await auth0.logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    }
                })
            }
        }

        const tryGetAccessToken = async (force: boolean, logoutOnFailure: boolean = false) => {
            const auth0 = getAuth0()

            if (!auth0?.isAuthenticated?.value) {
                return null
            }

            if (tokenExpiresAtMs && Date.now() > tokenExpiresAtMs) {
                await hardLogout()
                return null
            }

            try {
                const response = await auth0.getAccessTokenSilently({
                    authorizationParams: {
                        audience: Constants.AUTH0_AUDIENCE,
                    },
                    detailedResponse: true,
                    ...(force ? { cacheMode: 'off' } : {})
                })

                const accessToken = response?.access_token || response?.accessToken || response
                const expiresIn = response?.expires_in || response?.expiresIn || null

                await syncStoreToken(accessToken, expiresIn)
                return accessToken || null
            } catch (e) {
                console.error('Failed to get access token:', e)

                if (logoutOnFailure) {
                    await hardLogout()
                }

                return null
            }
        }

        const runKeepAliveTick = async () => {
            const auth0 = getAuth0()
            if (!auth0?.isAuthenticated?.value) {
                stopKeepAlive()
                return
            }

            if (tokenExpiresAtMs && Date.now() > tokenExpiresAtMs) {
                await hardLogout()
                return
            }

            if (!shouldRefreshSession()) {
                scheduleInactivityLogout()
                return
            }

            const inRefreshWindow = tokenExpiresAtMs
                ? Date.now() >= (tokenExpiresAtMs - tokenRefreshLeadMs)
                : true

            if (inRefreshWindow) {
                await tryGetAccessToken(true, false)
            }
        }

        const runTokenRefreshCheck = async () => {
            const auth0 = getAuth0()
            console.log('Running token refresh check. Auth0 isAuthenticated:', auth0?.isAuthenticated?.value, 'Token expires at:', tokenExpiresAtMs, 'Current time:', Date.now())
            if (!auth0?.isAuthenticated?.value) {
                return
            }

            if (tokenExpiresAtMs && Date.now() > tokenExpiresAtMs) {
                await hardLogout()
                return
            }

            if (!shouldRefreshSession()) {
                scheduleInactivityLogout()
                return
            }

            await tryGetAccessToken(true, false)

            if (auth0TokenExpiresIn && auth0TokenExpiresIn <= 0) {
                await hardLogout()
            }
        }

        const startKeepAlive = () => {
            stopKeepAlive()
            bindActivityListeners()
            scheduleInactivityLogout()

            keepAliveInterval = setInterval(() => {
                void runKeepAliveTick()
            }, KEEP_ALIVE_INTERVAL_MS)
        }

        watch(
            () => [store?.state?.inConference, router?.currentRoute?.value?.name, store?.state?.auth?.authorized],
            () => {
                scheduleInactivityLogout()
            },
            { immediate: true }
        )

        const reset = () => {
            initFinished = false
            initPromise = null
            stopKeepAlive()
            stopTokenRefreshTimer()
            stopInactivityLogoutTimer()
            unbindActivityListeners()
            auth0TokenExpiresIn = null
            tokenExpiresAtMs = null
            tokenRefreshLeadMs = 0
            inactivityLogoutEnabled = true
        }

        const service = {
            reset,

            async login(loginOptions: any = {}) {
                reset()

                const auth0 = getAuth0()
                const { authorizationParams = {}, appState, ...restLoginOptions } = loginOptions

                await auth0.loginWithRedirect({
                    ...restLoginOptions,
                    appState: {
                        targetUrl: router.currentRoute.value.fullPath,
                        ...(appState || {})
                    },
                    authorizationParams: {
                        redirect_uri: window.location.origin,
                        audience: Constants.AUTH0_AUDIENCE,
                        ...authorizationParams
                    }
                })
            },

            async logout(logoutOptions: any = {}) {
                reset()

                const auth0 = getAuth0()

                await store.dispatch('auth/logout').catch((err: any) => {
                    console.warn('Local logout cleanup failed', err)
                })

                await auth0.logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                        ...(logoutOptions.logoutParams || {})
                    },
                    ...logoutOptions
                })
            },

            async getAccessToken(force: boolean = false) {
                return tryGetAccessToken(force, true)
            },

            async refreshSession() {
                const auth0 = getAuth0()

                if (!auth0?.isAuthenticated?.value) {
                    return null
                }

                try {
                    await tryGetAccessToken(true, true)

                    return auth0?.user?.value || null
                } catch (e) {
                    console.error('Failed to refresh auth0 session:', e)
                    return null
                }
            },

            setInactivityLogoutEnabled(enabled: boolean) {
                setInactivityLogoutEnabled(enabled)
            },

            isInactivityLogoutEnabled() {
                return inactivityLogoutEnabled
            },

            isAuthenticated() {
                const auth0 = getAuth0()
                return !!auth0?.isAuthenticated?.value
            },

            isLoading() {
                const auth0 = getAuth0()
                return !!auth0?.isLoading?.value
            },

            getUser() {
                const auth0 = getAuth0()
                return auth0?.user?.value || null
            },

            getClaims() {
                const auth0 = getAuth0()
                return auth0?.idTokenClaims?.value || null
            },

            async init() {
                if (initFinished) return
                if (initPromise) return initPromise

                initPromise = new Promise((resolve) => {
                    let stop: null | (() => void) = null

                    const finalize = async () => {
                        try {
                            const auth0 = getAuth0()
                            const isAuthenticated = !!auth0?.isAuthenticated?.value

                            if (isAuthenticated) {
                                tokenExpiresAtMs = getTokenExpiryMs(store?.state?.auth?.token)

                                if (tokenExpiresAtMs && Date.now() >= tokenExpiresAtMs) {
                                    await hardLogout()
                                    return
                                }

                                const accessToken = await tryGetAccessToken(false, true)

                                if (accessToken) {
                                    await store.dispatch('auth/userMe', {
                                        token: accessToken,
                                        room: false
                                    })
                                    startKeepAlive()
                                } else {
                                    await hardLogout()
                                }
                            } else {
                                await store.dispatch('auth/logout')
                            }
                        } catch (e) {
                            console.error('ForumAuth init error:', e)

                            await hardLogout()
                        } finally {
                            initFinished = true
                            store.commit('nonpersist/setPrefetched', true)

                            if (stop) stop()
                            resolve()
                        }
                    }

                    stop = watch(
                        () => getAuth0()?.isLoading?.value,
                        async (loading) => {
                            if (loading !== false) return
                            await finalize()
                        },
                        { immediate: true }
                    )
                })

                return initPromise
            }
        }

        app.config.globalProperties.$forumAuth = service
        app.provide('forumAuth', service)
    }
}
